import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Upload,
  message,
  Space,
  Tag,
  Descriptions,
  Statistic,
  Row,
  Col,
  Divider,
  Tabs,
} from 'antd';
import { PlusOutlined, FileOutlined, EyeOutlined, DollarOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import api, { getImageUrl } from '../services/api';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';

const { TextArea } = Input;

const MiCajaChica = () => {
  const { user } = useAuth();
  const [cajaChica, setCajaChica] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [cobros, setCobros] = useState([]);
  const [ajustes, setAjustes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalCobroVisible, setModalCobroVisible] = useState(false);
  const [editingGasto, setEditingGasto] = useState(null);
  const [editingCobro, setEditingCobro] = useState(null);
  const [form] = Form.useForm();
  const [formCobro] = Form.useForm();
  const [comprobanteFile, setComprobanteFile] = useState([]);

  useEffect(() => {
    if (user && user.personal_id) {
      fetchCajaChica();
      fetchGastos();
      fetchCobros();
      fetchAjustes();
    }
  }, [user]);

  const fetchCajaChica = async () => {
    if (!user || !user.personal_id) return;

    try {
      const response = await api.get(`/caja-chica?personal_id=${user.personal_id}`);
      console.log('Response caja chica:', response.data); // Debug temporal

      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        // Combinar datos de efectivo y tarjeta en un solo objeto
        const cajaEfectivo = response.data.find(c => c.tipo === 'efectivo') || {};
        const cajaTarjeta = response.data.find(c => c.tipo === 'tarjeta') || {};

        const cajaCombinada = {
          ...cajaEfectivo,
          // Datos de efectivo
          monto_asignado: parseFloat(cajaEfectivo.monto_asignado || 0),
          monto_disponible: parseFloat(cajaEfectivo.monto_disponible || 0),
          // Datos de tarjeta
          tarjeta_combustible: parseFloat(cajaTarjeta.tarjeta_combustible || cajaEfectivo.tarjeta_combustible || 0),
          monto_disponible_tarjeta: parseFloat(cajaTarjeta.monto_disponible_tarjeta || 0),
          // IDs para referencia
          id_efectivo: cajaEfectivo.id,
          id_tarjeta: cajaTarjeta.id,
        };

        console.log('Caja combinada:', cajaCombinada); // Debug temporal
        setCajaChica(cajaCombinada);
      } else if (response.data && !Array.isArray(response.data) && Object.keys(response.data).length > 0) {
        // Si viene como objeto único (compatibilidad hacia atrás)
        setCajaChica(response.data);
      } else {
        console.log('No se encontraron cajas chicas'); // Debug temporal
        setCajaChica(null);
      }
    } catch (error) {
      console.error('Error al cargar caja chica:', error);
      setCajaChica(null);
    }
  };

  const fetchGastos = async () => {
    if (!user || !user.personal_id) return;

    setLoading(true);
    try {
      const response = await api.get(`/gastos-caja-chica?personal_id=${user.personal_id}`);
      setGastos(response.data || []);
    } catch (error) {
      message.error('Error al cargar gastos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCobros = async () => {
    if (!user || !user.personal_id) return;

    try {
      const response = await api.get(`/cobros-caja-chica?personal_id=${user.personal_id}`);
      setCobros(response.data || []);
    } catch (error) {
      console.error('Error al cargar cobros:', error);
    }
  };

  const fetchAjustes = async () => {
    if (!user || !user.personal_id) return;

    try {
      const response = await api.get(`/caja-chica?ajustes_personal_id=${user.personal_id}`);
      setAjustes(response.data || []);
    } catch (error) {
      console.error('Error al cargar ajustes:', error);
    }
  };

  const handleSubmit = async (values) => {
    if (!user || !user.personal_id) {
      message.warning('No se encontró información del personal');
      return;
    }

    try {
      // Subir comprobante si existe
      let comprobanteUrl = null;
      if (comprobanteFile.length > 0 && comprobanteFile[0].originFileObj) {
        const formData = new FormData();
        formData.append('imagenes[]', comprobanteFile[0].originFileObj);
        formData.append('tipo', 'gastos');

        try {
          const uploadResponse = await api.post('/gastos-caja-chica/upload-comprobante', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });
          if (uploadResponse.data.urls && uploadResponse.data.urls.length > 0) {
            comprobanteUrl = uploadResponse.data.urls[0];
          }
        } catch (error) {
          message.error('Error al subir comprobante');
          return;
        }
      }

      const data = {
        personal_id: user.personal_id,
        tipo_gasto: values.tipo_gasto,
        tipo_pago: values.tipo_pago || 'efectivo',
        monto: values.monto,
        descripcion: values.descripcion,
        fecha_gasto: values.fecha_gasto ? values.fecha_gasto.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        comprobante: comprobanteUrl || editingGasto?.comprobante || null,
        observaciones: values.observaciones,
        boleta_factura: values.boleta_factura || null,
      };

      if (editingGasto) {
        // Actualizar gasto existente
        await api.put(`/gastos-caja-chica/${editingGasto.id}`, data);
        message.success('Gasto actualizado exitosamente');
      } else {
        // Crear nuevo gasto
        await api.post('/gastos-caja-chica', data);
        message.success('Gasto registrado exitosamente');
      }

      setModalVisible(false);
      setEditingGasto(null);
      form.resetFields();
      setComprobanteFile([]);
      fetchGastos();
      fetchCajaChica();
      fetchAjustes();
      fetchAjustes();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleEdit = (gasto) => {
    setEditingGasto(gasto);
    form.setFieldsValue({
      tipo_gasto: gasto.tipo_gasto,
      tipo_pago: gasto.tipo_pago || 'efectivo',
      monto: gasto.monto,
      descripcion: gasto.descripcion,
      fecha_gasto: gasto.fecha_gasto ? dayjs(gasto.fecha_gasto) : dayjs(),
      observaciones: gasto.observaciones,
      boleta_factura: gasto.boleta_factura || '',
    });
    // Si hay comprobante, mostrar como archivo existente
    if (gasto.comprobante) {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost/backend';
      const base = baseURL.replace('/backend', '');
      let comprobanteUrl = gasto.comprobante;
      if (!comprobanteUrl.startsWith('http://') && !comprobanteUrl.startsWith('https://')) {
        if (comprobanteUrl.startsWith('/backend/')) {
          comprobanteUrl = base + comprobanteUrl;
        } else if (comprobanteUrl.startsWith('/')) {
          comprobanteUrl = base + '/backend' + comprobanteUrl;
        } else {
          comprobanteUrl = base + '/backend/uploads/gastos/' + comprobanteUrl;
        }
      }
      setComprobanteFile([{
        uid: '-1',
        name: 'comprobante.jpg',
        status: 'done',
        url: comprobanteUrl,
      }]);
    } else {
      setComprobanteFile([]);
    }
    setModalVisible(true);
  };

  const handleDelete = async (gastoId) => {
    Modal.confirm({
      title: '¿Estás seguro de eliminar este gasto?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.delete(`/gastos-caja-chica/${gastoId}`);
          message.success('Gasto eliminado exitosamente');
          fetchGastos();
          fetchCajaChica();
          fetchAjustes();
        } catch (error) {
          message.error(error.message);
        }
      },
    });
  };

  const handleDeleteCobro = async (cobroId) => {
    Modal.confirm({
      title: '¿Estás seguro de eliminar este cobro?',
      content: 'Esta acción restará el monto de la caja chica y no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.delete(`/cobros-caja-chica/${cobroId}`);
          message.success('Cobro eliminado exitosamente');
          fetchCobros();
          fetchCajaChica();
          fetchAjustes();
        } catch (error) {
          message.error(error.message);
        }
      },
    });
  };

  const handleSubmitCobro = async (values) => {
    if (!user || !user.personal_id || !cajaChica) {
      message.warning('No se encontró información del personal o caja chica');
      return;
    }

    try {
      const data = {
        personal_id: user.personal_id,
        monto: values.monto,
        descripcion: values.descripcion,
        fecha_cobro: values.fecha_cobro ? values.fecha_cobro.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        observaciones: values.observaciones,
      };

      if (editingCobro) {
        // Actualizar cobro existente
        await api.put(`/cobros-caja-chica/${editingCobro.id}`, data);
        message.success('Cobro actualizado exitosamente');
      } else {
        // Crear nuevo cobro
        await api.post('/cobros-caja-chica', data);
        message.success('Cobro registrado exitosamente');
      }

      setModalCobroVisible(false);
      setEditingCobro(null);
      formCobro.resetFields();
      fetchCajaChica();
      fetchCobros();
      fetchAjustes();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleEditCobro = (cobro) => {
    setEditingCobro(cobro);
    formCobro.setFieldsValue({
      monto: cobro.monto,
      descripcion: cobro.descripcion,
      fecha_cobro: cobro.fecha_cobro ? dayjs(cobro.fecha_cobro) : dayjs(),
      observaciones: cobro.observaciones,
    });
    setModalCobroVisible(true);
  };

  const getTipoGastoColor = (tipo) => {
    const colores = {
      operativo: 'blue',
      combustible: 'orange',
      peaje: 'green',
      otro: 'default',
    };
    return colores[tipo] || 'default';
  };

  const getEstadoColor = (estado) => {
    const colores = {
      pendiente: 'orange',
      aprobado: 'green',
      rechazado: 'red',
    };
    return colores[estado] || 'default';
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'fecha_gasto',
      key: 'fecha_gasto',
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Tipo de Gasto',
      dataIndex: 'tipo_gasto',
      key: 'tipo_gasto',
      render: (tipo) => (
        <Tag color={getTipoGastoColor(tipo)}>
          {tipo ? tipo.toUpperCase() : '-'}
        </Tag>
      ),
    },
    {
      title: 'Tipo de Pago',
      dataIndex: 'tipo_pago',
      key: 'tipo_pago',
      render: (tipo) => (
        <Tag color={tipo === 'tarjeta' ? 'purple' : 'default'}>
          {tipo === 'tarjeta' ? 'Tarjeta' : 'Efectivo'}
        </Tag>
      ),
    },
    {
      title: 'Monto',
      dataIndex: 'monto',
      key: 'monto',
      render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : '-',
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      render: (text) => text || '-',
    },
    {
      title: 'Boleta/Factura',
      dataIndex: 'boleta_factura',
      key: 'boleta_factura',
      render: (text) => text || '-',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => (
        <Tag color={getEstadoColor(estado)}>
          {estado === 'cancelado' ? 'ANULADO' : (estado ? estado.toUpperCase() : 'PENDIENTE')}
        </Tag>
      ),
    },
    {
      title: 'Comprobante',
      key: 'comprobante',
      render: (_, record) => {
        if (!record.comprobante) return '-';

        // Construir URL del comprobante
        let comprobanteUrl = record.comprobante;

        // Si ya es una URL absoluta, usarla directamente
        if (!comprobanteUrl.startsWith('http://') && !comprobanteUrl.startsWith('https://')) {
          // Si es una ruta relativa, construir la URL completa
          const baseURL = process.env.REACT_APP_API_URL || 'http://localhost/backend';
          const base = baseURL.replace('/backend', '');

          if (comprobanteUrl.startsWith('/backend/')) {
            comprobanteUrl = base + comprobanteUrl;
          } else if (comprobanteUrl.startsWith('/')) {
            comprobanteUrl = base + '/backend' + comprobanteUrl;
          } else {
            // Si no tiene prefijo, asumir que está en /backend/uploads/gastos/
            comprobanteUrl = base + '/backend/uploads/gastos/' + comprobanteUrl;
          }
        }

        return (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => window.open(comprobanteUrl, '_blank')}
          >
            Ver
          </Button>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={record.estado === 'aprobado'}
          >
            Editar
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            disabled={record.estado === 'aprobado'}
          >
            Eliminar
          </Button>
        </Space>
      ),
    },
  ];

  if (!user || !user.personal_id) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>
          <p>No se encontró información del personal. Por favor, contacta al administrador.</p>
        </Card>
      </div>
    );
  }

  const isMobile = window.innerWidth < 768;

  // Calcular gastos por tipo de pago
  const gastosEfectivo = gastos.filter(g => g.tipo_pago === 'efectivo' || !g.tipo_pago);
  const gastosTarjeta = gastos.filter(g => g.tipo_pago === 'tarjeta');

  // Solo contar gastos aprobados para el cálculo
  const gastosEfectivoAprobados = gastosEfectivo.filter(g => g.estado === 'aprobado');
  const gastosTarjetaAprobados = gastosTarjeta.filter(g => g.estado === 'aprobado');

  const totalGastosEfectivo = gastosEfectivoAprobados.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
  const totalGastosTarjeta = gastosTarjetaAprobados.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);

  // Efectivo disponible = monto disponible de la caja chica (ya incluye gastos y cobros)
  const efectivoDisponible = parseFloat(cajaChica?.monto_disponible || 0);

  // Saldo tarjeta = monto disponible de tarjeta (ya incluye gastos)
  const saldoTarjeta = parseFloat(cajaChica?.monto_disponible_tarjeta || 0);

  // Calcular total de cobros
  const totalCobros = cobros.reduce((sum, c) => sum + parseFloat(c.monto || 0), 0);

  // Calcular ajustes de saldo por tipo
  const ajustesEfectivo = ajustes.filter(a => a.tipo_caja === 'efectivo');
  const ajustesTarjeta = ajustes.filter(a => a.tipo_caja === 'tarjeta');

  const ajustesEfectivoAgregar = ajustesEfectivo
    .filter(a => a.tipo_ajuste === 'agregar')
    .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
  const ajustesEfectivoQuitar = ajustesEfectivo
    .filter(a => a.tipo_ajuste === 'quitar')
    .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);

  const ajustesTarjetaAgregar = ajustesTarjeta
    .filter(a => a.tipo_ajuste === 'agregar')
    .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);
  const ajustesTarjetaQuitar = ajustesTarjeta
    .filter(a => a.tipo_ajuste === 'quitar')
    .reduce((sum, a) => sum + parseFloat(a.monto || 0), 0);

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: isMobile ? '18px' : '24px' }}>Mi Caja Chica</h2>
        </div>

        {cajaChica ? (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Monto Asignado"
                    value={parseFloat(cajaChica.monto_asignado || 0).toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: '#1890ff', fontSize: isMobile ? '16px' : '24px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Gastos Efectivo"
                    value={totalGastosEfectivo.toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: '#722ed1', fontSize: isMobile ? '16px' : '20px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Efectivo Disponible"
                    value={efectivoDisponible.toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: '#3f8600', fontSize: isMobile ? '16px' : '24px' }}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Tarjeta Combustible"
                    value={parseFloat(cajaChica.tarjeta_combustible || 0).toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: '#fa8c16', fontSize: isMobile ? '16px' : '20px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Gastos Tarjeta"
                    value={totalGastosTarjeta.toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: '#eb2f96', fontSize: isMobile ? '16px' : '20px' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Saldo Tarjeta"
                    value={saldoTarjeta.toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: saldoTarjeta >= 0 ? '#52c41a' : '#ff4d4f', fontSize: isMobile ? '16px' : '20px' }}
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} md={8}>
                <Card>
                  <Statistic
                    title="Total Cobros"
                    value={totalCobros.toFixed(2)}
                    prefix={<DollarOutlined />}
                    suffix="S/"
                    valueStyle={{ color: '#13c2c2', fontSize: isMobile ? '16px' : '24px' }}
                  />
                </Card>
              </Col>
            </Row>

            {(ajustesEfectivoAgregar > 0 || ajustesEfectivoQuitar > 0 || ajustesTarjetaAgregar > 0 || ajustesTarjetaQuitar > 0) && (
              <>
                <Divider orientation="left">Incremento de Caja Chica</Divider>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  {ajustesEfectivoAgregar > 0 && (
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Incremento Efectivo (+)"
                          value={ajustesEfectivoAgregar.toFixed(2)}
                          prefix={<DollarOutlined />}
                          suffix="S/"
                          valueStyle={{ color: '#52c41a', fontSize: isMobile ? '16px' : '20px' }}
                        />
                      </Card>
                    </Col>
                  )}
                  {ajustesEfectivoQuitar > 0 && (
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Incremento Efectivo (-)"
                          value={ajustesEfectivoQuitar.toFixed(2)}
                          prefix={<DollarOutlined />}
                          suffix="S/"
                          valueStyle={{ color: '#ff4d4f', fontSize: isMobile ? '16px' : '20px' }}
                        />
                      </Card>
                    </Col>
                  )}
                  {ajustesTarjetaAgregar > 0 && (
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Incremento Tarjeta (+)"
                          value={ajustesTarjetaAgregar.toFixed(2)}
                          prefix={<DollarOutlined />}
                          suffix="S/"
                          valueStyle={{ color: '#52c41a', fontSize: isMobile ? '16px' : '20px' }}
                        />
                      </Card>
                    </Col>
                  )}
                  {ajustesTarjetaQuitar > 0 && (
                    <Col xs={24} sm={12} md={6}>
                      <Card>
                        <Statistic
                          title="Incremento Tarjeta (-)"
                          value={ajustesTarjetaQuitar.toFixed(2)}
                          prefix={<DollarOutlined />}
                          suffix="S/"
                          valueStyle={{ color: '#ff4d4f', fontSize: isMobile ? '16px' : '20px' }}
                        />
                      </Card>
                    </Col>
                  )}
                </Row>
              </>
            )}

            <Card
              title="Gastos"
              extra={
                <Space>
                  <Button
                    type="default"
                    icon={<DollarOutlined />}
                    onClick={() => {
                      setEditingCobro(null);
                      formCobro.resetFields();
                      formCobro.setFieldsValue({
                        fecha_cobro: dayjs(),
                      });
                      setModalCobroVisible(true);
                    }}
                  >
                    Agregar Cobro
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setEditingGasto(null);
                      form.resetFields();
                      form.setFieldsValue({
                        fecha_gasto: dayjs(),
                      });
                      setComprobanteFile([]);
                      setModalVisible(true);
                    }}
                  >
                    Nuevo Gasto
                  </Button>
                </Space>
              }
              style={{ marginBottom: 24 }}
            >
              <Tabs defaultActiveKey="1">
                <Tabs.TabPane tab="Todos los Gastos" key="1">
                  <Table
                    columns={columns}
                    dataSource={gastos}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                    size={isMobile ? 'small' : 'middle'}
                    footer={() => {
                      const totalTodosGastos = gastos.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
                      const totalTodosEfectivo = gastosEfectivo.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);
                      const totalTodosTarjeta = gastosTarjeta.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);

                      return (
                        <div style={{ fontWeight: 'bold', textAlign: 'right', padding: '8px' }}>
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ marginRight: '24px' }}>Total Gastos Efectivo: <Tag color="default">S/ {totalTodosEfectivo.toFixed(2)}</Tag></span>
                          </div>
                          <div style={{ marginBottom: '4px' }}>
                            <span style={{ marginRight: '24px' }}>Total Gastos Tarjeta: <Tag color="purple">S/ {totalTodosTarjeta.toFixed(2)}</Tag></span>
                          </div>
                          <div>
                            <span>Total General: <Tag color="blue">S/ {totalTodosGastos.toFixed(2)}</Tag></span>
                          </div>
                        </div>
                      );
                    }}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Gastos Efectivo" key="2">
                  <Table
                    columns={columns}
                    dataSource={gastosEfectivo}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                    size={isMobile ? 'small' : 'middle'}
                    footer={() => {
                      const totalEfectivo = gastosEfectivo.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);

                      return (
                        <div style={{ fontWeight: 'bold', textAlign: 'right', padding: '8px' }}>
                          <span>Total Gastos Efectivo: <Tag color="default">S/ {totalEfectivo.toFixed(2)}</Tag></span>
                        </div>
                      );
                    }}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Gastos Tarjeta" key="3">
                  <Table
                    columns={columns}
                    dataSource={gastosTarjeta}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                    size={isMobile ? 'small' : 'middle'}
                    footer={() => {
                      const totalTarjeta = gastosTarjeta.reduce((sum, g) => sum + parseFloat(g.monto || 0), 0);

                      return (
                        <div style={{ fontWeight: 'bold', textAlign: 'right', padding: '8px' }}>
                          <span>Total Gastos Tarjeta: <Tag color="purple">S/ {totalTarjeta.toFixed(2)}</Tag></span>
                        </div>
                      );
                    }}
                  />
                </Tabs.TabPane>
                <Tabs.TabPane tab="Cobros" key="4">
                  <Table
                    columns={[
                      {
                        title: 'Fecha',
                        dataIndex: 'fecha_cobro',
                        key: 'fecha_cobro',
                        render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
                      },
                      {
                        title: 'Monto',
                        dataIndex: 'monto',
                        key: 'monto',
                        render: (monto) => monto ? `S/ ${parseFloat(monto).toFixed(2)}` : '-',
                      },
                      {
                        title: 'Descripción',
                        dataIndex: 'descripcion',
                        key: 'descripcion',
                        render: (text) => text || '-',
                      },
                      {
                        title: 'Observaciones',
                        dataIndex: 'observaciones',
                        key: 'observaciones',
                        render: (text) => text || '-',
                      },
                      {
                        title: 'Acciones',
                        key: 'acciones',
                        render: (_, record) => (
                          <Space>
                            <Button
                              type="link"
                              icon={<EditOutlined />}
                              onClick={() => handleEditCobro(record)}
                            >
                              Editar
                            </Button>
                            <Button
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteCobro(record.id)}
                            >
                              Eliminar
                            </Button>
                          </Space>
                        ),
                      },
                    ]}
                    dataSource={cobros}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                    size={isMobile ? 'small' : 'middle'}
                    footer={() => {
                      const totalCobrosTabla = cobros.reduce((sum, c) => sum + parseFloat(c.monto || 0), 0);

                      return (
                        <div style={{ fontWeight: 'bold', textAlign: 'right', padding: '8px' }}>
                          <span>Total Cobros: <Tag color="blue">S/ {totalCobrosTabla.toFixed(2)}</Tag></span>
                        </div>
                      );
                    }}
                  />
                </Tabs.TabPane>
              </Tabs>
            </Card>
          </>
        ) : (
          <Card>
            <p>No tienes caja chica asignada. Contacta al administrador para que te asigne un monto.</p>
          </Card>
        )}

        <Modal
          title={editingGasto ? "Editar Gasto" : "Registrar Nuevo Gasto"}
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setEditingGasto(null);
            form.resetFields();
            setComprobanteFile([]);
          }}
          footer={null}
          width={isMobile ? '95%' : 600}
          style={{ top: isMobile ? 10 : 20 }}
          bodyStyle={{ maxHeight: isMobile ? '80vh' : 'auto', overflowY: 'auto' }}
        >
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item
              name="tipo_gasto"
              label="Tipo de Gasto"
              rules={[{ required: true, message: 'Selecciona el tipo de gasto' }]}
            >
              <Select placeholder="Selecciona el tipo de gasto">
                <Select.Option value="operativo">Operativo</Select.Option>
                <Select.Option value="combustible">Combustible</Select.Option>
                <Select.Option value="peaje">Peaje</Select.Option>
                <Select.Option value="otro">Otro</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="tipo_pago"
              label="Tipo de Pago"
              rules={[{ required: true, message: 'Selecciona el tipo de pago' }]}
            >
              <Select placeholder="Selecciona el tipo de pago">
                <Select.Option value="efectivo">Efectivo</Select.Option>
                <Select.Option value="tarjeta">Tarjeta</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="monto"
              label="Monto"
              rules={[{ required: true, message: 'Ingresa el monto' }]}
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="0.00"
              />
            </Form.Item>

            <Form.Item
              name="fecha_gasto"
              label="Fecha del Gasto"
              rules={[{ required: true, message: 'Selecciona la fecha' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              name="descripcion"
              label="Descripción"
              rules={[{ required: true, message: 'Ingresa la descripción' }]}
            >
              <TextArea rows={3} placeholder="Describe el gasto realizado" />
            </Form.Item>

            <Form.Item
              name="boleta_factura"
              label="Boleta/Factura"
            >
              <Input placeholder="Número de boleta o factura (opcional)" />
            </Form.Item>

            <Form.Item label="Comprobante">
              <Upload
                name="comprobante"
                listType="picture-card"
                fileList={comprobanteFile}
                onChange={({ fileList }) => {
                  setComprobanteFile(fileList);
                }}
                beforeUpload={() => false}
                accept="image/*,.pdf"
                maxCount={1}
                onPreview={(file) => {
                  window.open(file.url || file.thumbUrl, '_blank');
                }}
                customRequest={({ onSuccess }) => {
                  setTimeout(() => {
                    onSuccess("ok");
                  }, 0);
                }}
              >
                {comprobanteFile.length < 1 && (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Subir</div>
                  </div>
                )}
              </Upload>
              <p style={{ fontSize: '12px', color: '#999', marginTop: 8 }}>
                Sube una imagen o PDF del comprobante (máx. 10MB)
              </p>
            </Form.Item>

            <Form.Item name="observaciones" label="Observaciones">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingGasto ? 'Actualizar Gasto' : 'Registrar Gasto'}
                </Button>
                <Button
                  onClick={() => {
                    setModalVisible(false);
                    setEditingGasto(null);
                    form.resetFields();
                    setComprobanteFile([]);
                  }}
                >
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <Modal
          title={editingCobro ? "Editar Cobro" : "Agregar Cobro"}
          open={modalCobroVisible}
          onCancel={() => {
            setModalCobroVisible(false);
            setEditingCobro(null);
            formCobro.resetFields();
          }}
          footer={null}
          width={isMobile ? '95%' : 600}
          style={{ top: isMobile ? 10 : 20 }}
        >
          <Form form={formCobro} layout="vertical" onFinish={handleSubmitCobro}>
            <Form.Item
              name="monto"
              label="Monto del Cobro"
              rules={[{ required: true, message: 'Ingresa el monto' }]}
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                placeholder="0.00"
                prefix="S/"
              />
            </Form.Item>

            <Form.Item
              name="fecha_cobro"
              label="Fecha del Cobro"
              rules={[{ required: true, message: 'Selecciona la fecha' }]}
            >
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>

            <Form.Item
              name="descripcion"
              label="Descripción"
              rules={[{ required: true, message: 'Ingresa la descripción' }]}
            >
              <TextArea rows={3} placeholder="Describe el cobro recibido" />
            </Form.Item>

            <Form.Item name="observaciones" label="Observaciones">
              <TextArea rows={2} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {editingCobro ? 'Actualizar Cobro' : 'Registrar Cobro'}
                </Button>
                <Button
                  onClick={() => {
                    setModalCobroVisible(false);
                    setEditingCobro(null);
                    formCobro.resetFields();
                  }}
                >
                  Cancelar
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default MiCajaChica;

